import {
  PpmActionsModel,
  PpmCardsModel,
  PpmContextModel,
  PpmLineRow,
  PpmLookupRow,
  PpmRow,
  PpmWorkbenchModel,
} from './procurement.models';
const n = (v: any, f = 0) => (Number.isFinite(Number(v)) ? Number(v) : f);
const s = (v: any, f = '') => (v === null || v === undefined ? f : String(v));
const b = (v: any) =>
  v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true';

export const normalizePpm = (r: any): PpmRow => ({
  id: r.id ?? r.rid ?? r.recid,
  rid: n(r.rid ?? r.recid ?? r.id),
  rix: s(r.rix ?? r.recix),
  shc: s(r.shc ?? r.shortcode),
  nam: s(r.nam ?? r.recname),
  fyr: r.fyr ?? r.fiscalyear ?? '',
  psg: s(r.psg ?? r.ppmstage, 'PREAPPROVAL').toUpperCase(),
  pst: s(r.pst ?? r.status).toUpperCase(),
  tam: n(r.tam ?? r.totalamount ?? r.totalbudget),
  fam: n(r.fam ?? r.fonctionnement),
  iam: n(r.iam ?? r.investissement),
  lct: n(r.lct ?? r.linecount),
  bvi: r.bvi ?? r.budgetversionid ?? null,
  bvc: s(r.bvc ?? r.budgetversioncode),
  bvn: s(r.bvn ?? r.budgetversionname),
  aps: s(r.aps ?? r.approvalstage).toUpperCase(),
  stp: r.stp ?? r.stamp ?? null,
  raw: r,
});

export const normalizePpmLine = (r: any): PpmLineRow => ({
  id: r.id ?? r.rid ?? r.recid,
  rid: n(r.rid ?? r.recid ?? r.id),
  rix: s(r.rix ?? r.recix),
  ppi: n(r.ppi ?? r.ppmid),
  bvl: r.bvl ?? r.budgetversionlineid ?? null,
  sai: r.sai ?? r.superactivityid ?? null,
  sac: s(r.sac ?? r.superactivitycode),
  san: s(r.san ?? r.superactivityname),
  psc: s(r.psc ?? r.ppmsection),
  ccc: s(r.ccc ?? r.costcentercode),
  ccn: s(r.ccn ?? r.costcentername),
  acc: s(r.acc ?? r.accountcode),
  acn: s(r.acn ?? r.accountname),
  nam: s(r.nam ?? r.recname ?? r.activity),
  dsc: s(r.dsc ?? r.description),
  bga: n(r.bga ?? r.budgetamount),
  ara: n(r.ara ?? r.arrears),
  avc: n(r.avc ?? r.availablecredit),
  atx: s(r.atx ?? r.activitytext),
  pnt: s(r.pnt ?? r.nature),
  pmd: s(r.pmd ?? r.method),
  rvt: s(r.rvt ?? r.reviewtype),
  tpd: s(r.tpd ?? r.publicationdate),
  bsd: s(r.bsd ?? r.biddate),
  evd: n(r.evd ?? r.evaluationdays),
  psd: s(r.psd ?? r.startdate),
  exd: n(r.exd ?? r.executiondays),
  eed: s(r.eed ?? r.enddate),
  lot: s(r.lot ?? r.lotno),
  ruc: s(r.ruc ?? r.responsibleunit),
  pst: s(r.pst ?? r.status).toUpperCase(),
  stp: r.stp ?? r.stamp ?? null,
  _state: r._state ?? 'clean',
  __dirty: false,
  __error: '',
  raw: r,
});

export const normalizeLookup = (r: any): PpmLookupRow => ({
  rid: r.rid ?? r.recid ?? r.id,
  shc: s(r.shc ?? r.shortcode ?? r.code),
  nam: s(r.nam ?? r.recname ?? r.name),
  fyr: r.fyr ?? r.fiscalyear ?? '',
  pst: s(r.pst ?? r.status).toUpperCase(),
  aps: s(r.aps ?? r.approvalstage).toUpperCase(),
  psg: s(r.psg ?? r.ppmstage).toUpperCase(),
});

export const normalizePpmCards = (r: any): PpmCardsModel => ({
  total: n(r?.total ?? r?.tam),
  fonctionnement: n(r?.fonctionnement ?? r?.fam),
  investissement: n(r?.investissement ?? r?.iam),
  linecount: n(r?.linecount ?? r?.lct),
  superactivitycount: n(r?.superactivitycount),
  scheduledcount: n(r?.scheduledcount),
  unscheduledcount: n(r?.unscheduledcount),
  status: s(r?.status ?? r?.pst),
});

export const normalizePpmContext = (r: any): PpmContextModel => ({
  planningcycleid: n(r?.planningcycleid ?? r?.pci),
  planningcyclecode: s(r?.planningcyclecode ?? r?.pcc),
  planningcyclename: s(r?.planningcyclename ?? r?.pcn),
  fiscalyear: r?.fiscalyear ?? r?.fyr,
  budgetversionid: r?.budgetversionid ?? r?.bvi ?? null,
  budgetversioncode: s(r?.budgetversioncode ?? r?.bvc),
  budgetversionname: s(r?.budgetversionname ?? r?.bvn),
  budgetversionmilestone: s(r?.budgetversionmilestone ?? r?.bvm),
  budgetversionstate: s(r?.budgetversionstate ?? r?.bvs),
  phasecode: s(r?.phasecode),
  stamp: r?.stamp ?? r?.stp,
});

export const normalizePpmActions = (r: any): PpmActionsModel => ({
  canGenerate: b(r?.canGenerate),
  canEdit: b(r?.canEdit),
  canCalendarize: b(r?.canCalendarize),
  canSubmit: b(r?.canSubmit),
  canValidateDraft: b(r?.canValidateDraft),
  canFinalize: b(r?.canFinalize),
  canViewReport: b(r?.canViewReport ?? true),
  canExport: b(r?.canExport ?? true),
  nextpage: r?.nextpage ?? null,
});

export const normalizePpmWorkbench = (raw: any): PpmWorkbenchModel => {
  const x = raw?.sd ?? raw ?? {};
  const plans = Array.isArray(x.plans ?? x.ppms)
    ? (x.plans ?? x.ppms).map(normalizePpm)
    : [];
  const selected = x.selectedPpm
    ? normalizePpm(x.selectedPpm)
    : (plans[0] ?? null);
  return {
    context: normalizePpmContext(x.context),
    actions: normalizePpmActions(x.actions),
    cards: normalizePpmCards(x.cards),
    plans,
    selectedPpm: selected,
    lines: Array.isArray(x.lines) ? x.lines.map(normalizePpmLine) : [],
  };
};

// export const normalizePpmReportRow = (r: any) => ({
//   id: r.rid,
//   rid: Number(r.rid ?? 0),

//   section: String(r.section ?? ''),
//   sectionorder: Number(r.sectionorder ?? 0),
//   lineorder: Number(r.lineorder ?? 0),
//   lineno: Number(r.lineno ?? 0),

//   budgetline: String(r.budgetline ?? ''),
//   initialbudget: Number(r.initialbudget ?? 0),
//   arrears: Number(r.arrears ?? 0),
//   availablecredits: Number(r.availablecredits ?? 0),

//   activity: String(r.activity ?? ''),
//   prestationnature: String(r.prestationnature ?? ''),
//   procurementmethod: String(r.procurementmethod ?? ''),
//   reviewtype: String(r.reviewtype ?? ''),

//   tenderpublicationdate: r.tenderpublicationdate ?? '',
//   bidsubmissiondate: r.bidsubmissiondate ?? '',
//   evaluationdays: Number(r.evaluationdays ?? 0),

//   probablestartdate: r.probablestartdate ?? '',
//   executiondays: Number(r.executiondays ?? 0),
//   expectedenddate: r.expectedenddate ?? '',

//   lotno: String(r.lotno ?? ''),
//   responsibleunit: String(r.responsibleunit ?? ''),
//   superactivitycode: String(r.superactivitycode ?? ''),
//   superactivityname: String(r.superactivityname ?? ''),
//   linestatus: String(r.linestatus ?? ''),
// });

export const normalizePpmReportRow = (r: any) => ({
  id: r.id ?? r.rid ?? r.recid,
  rid: n(r.rid ?? r.recid ?? r.id),

  section: s(r.section),
  sectionorder: n(r.sectionorder),
  lineorder: n(r.lineorder),
  lineno: n(r.lineno),

  budgetline: s(r.budgetline),
  initialbudget: n(r.initialbudget),
  arrears: n(r.arrears),
  availablecredits: n(r.availablecredits),

  activity: s(r.activity),
  prestationnature: s(r.prestationnature),
  procurementmethod: s(r.procurementmethod),
  reviewtype: s(r.reviewtype),

  tenderpublicationdate: s(r.tenderpublicationdate),
  bidsubmissiondate: s(r.bidsubmissiondate),
  evaluationdays: n(r.evaluationdays),

  probablestartdate: s(r.probablestartdate),
  executiondays: n(r.executiondays),
  expectedenddate: s(r.expectedenddate),

  lotno: s(r.lotno),
  responsibleunit: s(r.responsibleunit),

  superactivitycode: s(r.superactivitycode),
  superactivityname: s(r.superactivityname),

  linestatus: s(r.linestatus).toUpperCase(),

  raw: r,
});
